import type { Theme } from '@material-ui/core'
import type { CSSProperties } from '@material-ui/styles'

// Lucide "user" icon. A TipTap mark can only wrap text, so the icon can't be a child <svg> of the
// chip — it is drawn by `::before` as a mask filled with the chip's text colour instead.
const PERSON_ICON =
  // cspell:disable-next-line -- URL-encoded SVG markup
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/%3E%3Ccircle cx='12' cy='7' r='4'/%3E%3C/svg%3E\")"

/** Chip colours from the Placeholder Fallbacks handoff (docs/design/placeholder-fallbacks/). */
export const TEMPLATE_PLACEHOLDER_COLORS = {
  background: '#E7EFFC',
  border: '#CBDFFF',
  text: '#0559D2',
  /** Behind a fallback shown in a preview because the value was missing. */
  fallbackBackground: '#FDF3E3'
} as const

/**
 * The chip look for a `{{key}}` template placeholder. Lives here rather than inside the editor's
 * styles because placeholders render in two places: inside the RTE while a field is being edited,
 * and as read-only `HtmlContent` everywhere else that wants to *show* tags without resolving them
 * (see `templateToPlaceholderHtml`). One definition so the two can't drift apart visually.
 *
 * Consumers nest it under whatever selector owns the markup, e.g. `'& .template-placeholder'`.
 */
export const templatePlaceholderStyles = (_theme: Theme): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '0 6px',
  margin: '0 1px',
  borderRadius: 4,
  backgroundColor: TEMPLATE_PLACEHOLDER_COLORS.background,
  border: `1px solid ${TEMPLATE_PLACEHOLDER_COLORS.border}`,
  color: TEMPLATE_PLACEHOLDER_COLORS.text,
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.72em',
  fontWeight: 500,
  fontStyle: 'normal',
  textDecoration: 'none',
  lineHeight: 1.7,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  // Clickable so an editor can open the chip's edit popover (EditorPlaceholderPicker).
  cursor: 'pointer',
  userSelect: 'none',
  '&::before': {
    content: '""',
    flexShrink: 0,
    width: 11,
    height: 11,
    backgroundColor: 'currentColor',
    maskImage: PERSON_ICON,
    WebkitMaskImage: PERSON_ICON,
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat'
  }
})

/** A fallback that stood in for a missing value — highlighted in previews only. */
export const templateFallbackStyles: CSSProperties = {
  backgroundColor: TEMPLATE_PLACEHOLDER_COLORS.fallbackBackground,
  borderRadius: 2
}

/**
 * Placeholders are taller than plain text, so lines containing one need a little extra leading to
 * stop the chip border colliding with the line above.
 */
export const templatePlaceholderLineHeight = { lineHeight: 1.5 }
